import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
} from '@mui/material';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import api from '../api';
import { useToast } from '../hooks/useToast';

interface Digest {
  digest_id: string;
  week_start: string;
  week_end: string;
  compliment_count: number;
  summary_text: string;
  reported: boolean;
  created_at: string;
}

interface GoodVibesDigestProps {
  digests: Digest[];
  onRefresh?: () => void;
}

const GoodVibesDigest: React.FC<GoodVibesDigestProps> = ({ digests, onRefresh }) => {
  const { showToast } = useToast();
  const [reportDialog, setReportDialog] = useState<string | null>(null);
  const [reportReason, setReportReason] = useState('');
  const [reporting, setReporting] = useState(false);

  if (digests.length === 0) return null;

  const handleReport = async () => {
    if (!reportDialog) return;
    setReporting(true);
    try {
      await api.post(`/api/compliments/digests/${reportDialog}/report`, {
        reason: reportReason.trim() || null,
      });
      showToast('Report submitted. Thank you.', 'success');
      setReportDialog(null);
      setReportReason('');
      onRefresh?.();
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Failed to report', 'error');
    } finally {
      setReporting(false);
    }
  };

  const formatDateRange = (start: string, end: string) => {
    const s = new Date(start);
    const e = new Date(end);
    return `${s.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} — ${e.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`;
  };

  return (
    <>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <FavoriteBorderIcon sx={{ color: "primary.main" }} />
          <Typography variant="h6" fontWeight="bold">
            Good Vibes
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Anonymous appreciation from people who viewed your profile.
        </Typography>

        {digests.map((digest) => (
          <Paper
            key={digest.digest_id}
            variant="outlined"
            sx={{
              p: 2,
              mb: 1.5,
              backgroundColor: '#FEF2F2',
              borderColor: '#FECACA',
              '&:last-child': { mb: 0 },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Chip
                label={formatDateRange(digest.week_start, digest.week_end)}
                size="small"
                sx={{ backgroundColor: 'rgba(220,38,38,0.1)', color: "primary.main", fontWeight: 500 }}
              />
              <Typography variant="caption" color="text.secondary">
                {digest.compliment_count} {digest.compliment_count === 1 ? 'vibe' : 'vibes'}
              </Typography>
            </Box>

            <Typography variant="body1" sx={{ mb: 1.5 }}>
              {digest.summary_text}
            </Typography>

            {!digest.reported && (
              <Button
                size="small"
                color="inherit"
                startIcon={<FlagOutlinedIcon sx={{ fontSize: 14 }} />}
                onClick={() => setReportDialog(digest.digest_id)}
                sx={{ color: 'text.secondary', fontSize: 12 }}
              >
                Report
              </Button>
            )}
            {digest.reported && (
              <Typography variant="caption" color="text.secondary">
                Reported — under review
              </Typography>
            )}
          </Paper>
        ))}
      </Paper>

      <Dialog open={!!reportDialog} onClose={() => setReportDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Report this digest?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            If this digest contains anything inappropriate, let us know.
          </Typography>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            placeholder="What's wrong with this digest? (optional)"
            value={reportReason}
            onChange={(e) => setReportReason(e.target.value)}
            inputProps={{ maxLength: 500 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportDialog(null)} color="inherit">
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleReport} disabled={reporting}>
            {reporting ? 'Reporting...' : 'Report'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GoodVibesDigest;
